import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, RefreshCw } from "lucide-react";
import { getSubjectDisplayName } from "./utils";

export function AddSubjectModal({
  open,
  onOpenChange,
  selectedSubject,
  selectedDuration,
  setSelectedDuration,
  getSelectedPricing,
  onConfirm,
  loading
}) {
  const pricing = getSelectedPricing();
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Subject</DialogTitle>
          <DialogDescription>
            Choose a subscription duration for {selectedSubject?.display_name || getSubjectDisplayName(selectedSubject?.subject)}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Duration Selection */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Subscription Duration
            </label>
            <Select value={selectedDuration} onValueChange={setSelectedDuration}>
              <SelectTrigger className="w-full" data-testid="duration-select">
                <SelectValue placeholder="Select duration" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly" data-testid="duration-monthly">
                  <div className="flex items-center justify-between w-full">
                    <span>Monthly</span>
                  </div>
                </SelectItem>
                <SelectItem value="3_months" data-testid="duration-3-months">
                  <div className="flex items-center gap-2">
                    <span>3 Months</span>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">5% off</span>
                  </div>
                </SelectItem>
                <SelectItem value="6_months" data-testid="duration-6-months">
                  <div className="flex items-center gap-2">
                    <span>6 Months</span>
                    <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">10% off</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Pricing Preview */}
          {pricing && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Duration</span>
                <span className="font-medium">{pricing.duration_display}</span>
              </div>
              {pricing.discount_percent > 0 && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Original Price</span>
                    <span className="line-through text-gray-400">₹{pricing.total_before_discount}</span>
                  </div>
                  <div className="flex justify-between text-sm text-emerald-600">
                    <span>Discount ({pricing.discount_percent}%)</span>
                    <span>-₹{pricing.discount_amount}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span>Total</span>
                <span className="text-emerald-600">₹{pricing.total_price}</span>
              </div>
              <div className="text-xs text-gray-500 text-right">
                (₹{pricing.per_month}/month)
              </div>
            </div>
          )}
        </div>
        
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
            onClick={onConfirm}
            disabled={loading}
            data-testid="confirm-add-subject-btn"
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Add Subject
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default AddSubjectModal;
